import React, { useState, createContext, useContext, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, BACKEND_URL } from "../config";

const MusicPlayerContext = createContext();
const CartContext = createContext();
const FanClubContext = createContext();
const SettingsContext = createContext();

export const useMusicPlayer = () => useContext(MusicPlayerContext);
export const useCart = () => useContext(CartContext);
export const useFanClub = () => useContext(FanClubContext);
export const useSettings = () => useContext(SettingsContext);

const FETCH_SETTINGS_INTERVAL_MS = 30 * 1000;

const MusicPlayerProvider = ({ children }) => {
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [playlist, setPlaylist] = useState([]);
  const [currentAlbum, setCurrentAlbum] = useState(null);
  const [showLyrics, setShowLyrics] = useState(false);
  const audioRef = useRef(null);

  const playTrack = (track, album, tracks) => {
    setCurrentTrack(track);
    setCurrentAlbum(album);
    setPlaylist(tracks || []);
    setIsPlaying(true);
  };

  const togglePlay = () => setIsPlaying(!isPlaying);
  const toggleLyrics = () => setShowLyrics(!showLyrics);

  const nextTrack = () => {
    if (!currentTrack || playlist.length === 0) return;
    const currentIndex = playlist.findIndex(t => t.id === currentTrack.id);
    const nextIndex = (currentIndex + 1) % playlist.length;
    setCurrentTrack(playlist[nextIndex]);
    setIsPlaying(true);
  };

  const prevTrack = () => {
    if (!currentTrack || playlist.length === 0) return;
    const currentIndex = playlist.findIndex(t => t.id === currentTrack.id);
    const prevIndex = currentIndex === 0 ? playlist.length - 1 : currentIndex - 1;
    setCurrentTrack(playlist[prevIndex]);
    setIsPlaying(true);
  };

  const seekTo = (time) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setProgress(time);
    }
  };

  const toggleMute = () => setIsMuted(!isMuted);

  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) audioRef.current.play().catch(() => setIsPlaying(false));
      else audioRef.current.pause();
    }
  }, [isPlaying, currentTrack]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = isMuted ? 0 : volume;
  }, [volume, isMuted]);

  return (
    <MusicPlayerContext.Provider value={{
      currentTrack, isPlaying, progress, duration, volume, isMuted, currentAlbum, showLyrics,
      playTrack, togglePlay, nextTrack, prevTrack, seekTo, setVolume, toggleMute, toggleLyrics,
      setProgress, setDuration, audioRef
    }}>
      {children}
      {currentTrack && (
        <audio ref={audioRef} src={`${BACKEND_URL}/storage/${currentTrack.preview_url}`}
          onTimeUpdate={(e) => setProgress(e.target.currentTime)}
          onLoadedMetadata={(e) => setDuration(e.target.duration)}
          onEnded={nextTrack} />
      )}
    </MusicPlayerContext.Provider>
  );
};

const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  const matchCartItem = (i, productId, size, color) =>
    i.product_id === productId && (i.size ?? null) === (size ?? null) && (i.color ?? null) === (color ?? null);

  const addToCart = (item) => {
    setCartItems(prev => {
      const existing = prev.find(i => matchCartItem(i, item.product_id, item.size, item.color));
      if (existing) {
        return prev.map(i => matchCartItem(i, item.product_id, item.size, item.color) ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, { ...item, quantity: 1 }];
    });
    toast.success(`${item.product_name} hozzáadva a kosárhoz`);
  };

  const removeFromCart = (productId, size, color) => setCartItems(prev => prev.filter(i => !matchCartItem(i, productId, size, color)));
  const updateQuantity = (productId, size, color, quantity) => {
    if (quantity <= 0) { removeFromCart(productId, size, color); return; }
    setCartItems(prev => prev.map(i => matchCartItem(i, productId, size, color) ? { ...i, quantity } : i));
  };
  const clearCart = () => setCartItems([]);
  const cartTotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{ cartItems, cartTotal, cartCount, isCartOpen, addToCart, removeFromCart, updateQuantity, clearCart, setIsCartOpen }}>
      {children}
    </CartContext.Provider>
  );
};

const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    shop_enabled: false,
    fanclub_enabled: false,
    concerts_enabled: false,
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    og_title: '',
    og_description: '',
    og_image: '',
    og_type: 'website',
    social_facebook: '',
    social_youtube: '',
    social_instagram: '',
    social_spotify: '',
    social_tiktok: '',
    social_discord: '',
    _loaded: false,  // jelzi, hogy már betöltődtek-e a beállítások
  });

  const fetchSettings = useCallback(() => {
    axios.get(`${API}/settings`)
      .then(res => setSettings(prev => ({ ...res.data, _loaded: true })))
      .catch(() => setSettings(prev => ({ ...prev, _loaded: true })));  // hiba esetén is jelöljük betöltöttnek
  }, []);

  useEffect(() => {
    fetchSettings();
    const interval = setInterval(fetchSettings, FETCH_SETTINGS_INTERVAL_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') fetchSettings();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [fetchSettings]);

  return (
    <SettingsContext.Provider value={settings}>
      {children}
    </SettingsContext.Provider>
  );
};

const FanClubProvider = ({ children }) => {
  const [member, setMember] = useState(null);
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    axios.get(`${API}/fan-club/tiers`).then(res => setTiers(res.data)).catch(console.error);
    const saved = localStorage.getItem('fanClubMember');
    if (saved) setMember(JSON.parse(saved));
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/fan-club/login`, { email, password });
    setMember(res.data.member);
    localStorage.setItem('fanClubMember', JSON.stringify(res.data.member));
    return res.data;
  };

  const register = async (data) => {
    const res = await axios.post(`${API}/fan-club/register`, data);
    setMember(res.data.member);
    localStorage.setItem('fanClubMember', JSON.stringify(res.data.member));
    return res.data;
  };

  const createSubscriptionCheckout = async (data) => {
    const originUrl = window.location.origin;
    const res = await axios.post(`${API}/fan-club/subscription-checkout`, {
      ...data,
      origin_url: originUrl,
    });
    if (res.data.is_free && res.data.member) {
      setMember(res.data.member);
      localStorage.setItem('fanClubMember', JSON.stringify(res.data.member));
    }
    return res.data;
  };

  const completeSubscription = async (sessionId) => {
    const res = await axios.get(`${API}/fan-club/subscription-success`, {
      params: { session_id: sessionId },
    });
    setMember(res.data.member);
    localStorage.setItem('fanClubMember', JSON.stringify(res.data.member));
    return res.data;
  };

  const createUpgradeCheckout = async (tierId) => {
    const originUrl = window.location.origin;
    const res = await axios.post(`${API}/fan-club/upgrade-checkout`, {
      member_id: member?.id,
      tier_id: tierId,
      origin_url: originUrl,
    });
    if (res.data.is_free && res.data.member) {
      setMember(res.data.member);
      localStorage.setItem('fanClubMember', JSON.stringify(res.data.member));
      return res.data;
    }
    return res.data;
  };

  const logout = () => {
    setMember(null);
    localStorage.removeItem('fanClubMember');
  };

  const getMyAlbums = async () => {
    const token = member?.api_token;
    if (!token) return { albums: [] };
    try {
      const res = await axios.get(`${API}/fan-club/my-albums`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    } catch (err) {
      return { albums: [] };
    }
  };

  const authHeaders = () => (member?.api_token ? { Authorization: `Bearer ${member.api_token}` } : {});

  const getProfile = async () => {
    if (!member?.api_token) return null;
    try {
      const res = await axios.get(`${API}/fan-club/profile`, { headers: authHeaders() });
      if (res.data.member) {
        setMember(res.data.member);
        localStorage.setItem('fanClubMember', JSON.stringify(res.data.member));
      }
      return res.data.member;
    } catch (err) {
      return null;
    }
  };

  const getMyOrders = async () => {
    if (!member?.api_token) return { orders: [] };
    try {
      const res = await axios.get(`${API}/fan-club/my-orders`, { headers: authHeaders() });
      return res.data;
    } catch (err) {
      return { orders: [] };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    if (!member?.api_token) throw new Error('Nincs bejelentkezve');
    const res = await axios.post(
      `${API}/fan-club/change-password`,
      { current_password: currentPassword, password: newPassword, password_confirmation: newPassword },
      { headers: authHeaders() }
    );
    return res.data;
  };

  const getDiscordConnectUrl = async () => {
    if (!member?.api_token) throw new Error('Nincs bejelentkezve');
    const res = await axios.get(`${API}/discord/connect`, { headers: authHeaders() });
    return res.data?.redirect_url;
  };

  return (
    <FanClubContext.Provider value={{ member, tiers, login, register, createSubscriptionCheckout, completeSubscription, createUpgradeCheckout, logout, getMyAlbums, getProfile, getMyOrders, changePassword, getDiscordConnectUrl }}>
      {children}
    </FanClubContext.Provider>
  );
};

export const AppProviders = ({ children }) => (
  <SettingsProvider>
    <MusicPlayerProvider>
      <CartProvider>
        <FanClubProvider>
          {children}
        </FanClubProvider>
      </CartProvider>
    </MusicPlayerProvider>
  </SettingsProvider>
);
